import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  manterSelecionadaNaLista,
  manterSelecionadaNaPosicao,
} from '@/shared/utils/manterSelecionadaNaLista';

import type {
  BotFlowsElegiveisEntry,
  ConversationSegmentCounts,
  ConversaMode,
  InternalChatActiveListItem,
  InternalChatConversa,
  InternalChatConversaDetails,
  InternalChatLinha,
  InternalChatVinculo,
  MensagemDirecao,
  MensagemStatus,
} from '../../types';
import { botFlowsElegiveisQueryKey } from '../../utils/botFlowsElegiveis';
import { resumeConversationTemplate, sendMessage } from '../messages/actions';
import {
  archiveConversation,
  assignConversationResponsavel,
  assumirAtendimento,
  fetchBotFlowsElegiveis,
  fetchConversationDetails,
  fetchConversations,
  fetchLinhas,
  loadMoreConversations,
  retomarBot,
  transferConversation,
  unarchiveConversation,
  updateLinha,
} from './actions';

function markListDirty(state: ConversationsState): void {
  state.listDirtySeq += 1;
}

function patchListLastMessage(
  state: ConversationsState,
  payload: {
    conversaId: string;
    ultimaMensagem: string | null;
    dataUltimaMensagem: string | null;
    isInbound: boolean;
    // Catch-up de reconexão é RESSINCRONIZAÇÃO, não evento novo: reaplica a última mensagem
    // sem mexer no contador. Sem isto, `isInbound: false` (o que o catch-up passa) zeraria a
    // bolinha da conversa aberta sem ninguém ter respondido — COR-01 do review do #1100.
    preservaPendentes?: boolean;
    ultimoResponsavelNome?: string | null;
    ultimaMensagemId?: string | null;
    ultimaMensagemStatus?: MensagemStatus | null;
    ultimaMensagemDirecao?: MensagemDirecao | null;
  },
): void {
  const idx = state.items.findIndex((c) => c.id === payload.conversaId);
  if (idx < 0) {
    markListDirty(state);
    return;
  }
  const current = state.items[idx];
  if (!current) return;
  // Sem o recorte por conversa selecionada: abrir a conversa deixou de zerar o contador, então
  // a mensagem que chega com ela aberta continua pendente até respondermos.
  const pendentes = payload.preservaPendentes
    ? current.mensagensPendentes
    : payload.isInbound
      ? current.mensagensPendentes + 1
      : 0;
  const updated: InternalChatActiveListItem = {
    ...current,
    ultimaMensagem: payload.ultimaMensagem,
    dataUltimaMensagem: payload.dataUltimaMensagem,
    mensagensPendentes: pendentes,
    ...(payload.ultimoResponsavelNome !== undefined
      ? { ultimoResponsavelNome: payload.ultimoResponsavelNome }
      : {}),
    ...(payload.ultimaMensagemId !== undefined
      ? { ultimaMensagemId: payload.ultimaMensagemId }
      : {}),
    ...(payload.ultimaMensagemStatus !== undefined
      ? { ultimaMensagemStatus: payload.ultimaMensagemStatus }
      : {}),
    ...(payload.ultimaMensagemDirecao !== undefined
      ? { ultimaMensagemDirecao: payload.ultimaMensagemDirecao }
      : {}),
  };
  if (state.sortMode === 'queue') {
    state.items[idx] = updated;
    if (payload.isInbound && !current.awaitingSince) markListDirty(state);
    return;
  }
  const next = state.items.filter((_, i) => i !== idx);
  next.unshift(updated);
  state.items = next;
}

type ConversationsSortMode = 'recent' | 'queue';

interface ConversationsState {
  items: InternalChatActiveListItem[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextCursor: string | null;
  selectedId: string | null;
  selectedDetails: InternalChatConversaDetails | null;
  isLoadingList: boolean;
  isLoadingMore: boolean;
  isLoadingDetails: boolean;
  errorList: string | null;
  errorDetails: string | null;
  linhas: InternalChatLinha[];
  botFlowsElegiveis: Record<string, BotFlowsElegiveisEntry>;
  listDirtySeq: number;
  sortMode: ConversationsSortMode;
  latestListRequestId: string | null;
  segmentCounts: ConversationSegmentCounts;
}

const initialState: ConversationsState = {
  items: [],
  total: 0,
  limit: 50,
  offset: 0,
  hasMore: false,
  nextCursor: null,
  selectedId: null,
  selectedDetails: null,
  isLoadingList: false,
  isLoadingMore: false,
  isLoadingDetails: false,
  errorList: null,
  errorDetails: null,
  linhas: [],
  botFlowsElegiveis: {},
  listDirtySeq: 0,
  sortMode: 'queue',
  latestListRequestId: null,
  segmentCounts: { atendimento: 0, bot: 0, finalizados: 0 },
};

function applyConversaModeResult(state: ConversationsState, conversa: InternalChatConversa): void {
  const item = state.items.find((c) => c.id === conversa.id);
  if (item) {
    item.mode = conversa.mode;
    item.humanSupportReason = conversa.humanSupportReason ?? null;
    item.humanSupportSummary = conversa.humanSupportSummary ?? null;
  }
  if (state.selectedDetails?.id === conversa.id) {
    state.selectedDetails.mode = conversa.mode;
    state.selectedDetails.humanSupportReason = conversa.humanSupportReason ?? null;
    state.selectedDetails.humanSupportSummary = conversa.humanSupportSummary ?? null;
  }
}

const conversationsSlice = createSlice({
  name: 'chatAtendimentoConversations',
  initialState,
  reducers: {
    selectConversation: (state, action: { payload: string | null }) => {
      state.selectedId = action.payload;
      if (state.selectedDetails?.id !== action.payload) {
        state.selectedDetails = null;
      }
    },
    clearListError: (state) => {
      state.errorList = null;
    },
    clearDetailsError: (state) => {
      state.errorDetails = null;
    },
    setSortMode: (state, action: PayloadAction<ConversationsSortMode>) => {
      state.sortMode = action.payload;
    },
    realtimeConversationUpserted: (
      state,
      action: {
        payload: {
          conversaId: string;
          ultimaMensagem: string | null;
          dataUltimaMensagem: string | null;
          isInbound: boolean;
          preservaPendentes?: boolean;
          ultimoResponsavelNome?: string | null;
          ultimaMensagemId?: string | null;
          ultimaMensagemStatus?: MensagemStatus | null;
          ultimaMensagemDirecao?: MensagemDirecao | null;
        };
      },
    ) => {
      patchListLastMessage(state, action.payload);
    },
    realtimeConversationMessageStatus: (
      state,
      action: PayloadAction<{
        conversaId: string;
        status: MensagemStatus;
        messageId?: string;
      }>,
    ) => {
      const item = state.items.find((c) => c.id === action.payload.conversaId);
      if (!item) return;
      if (item.ultimaMensagemDirecao !== 'OUTBOUND') return;
      const { messageId, status } = action.payload;
      if (item.ultimaMensagemId && messageId && item.ultimaMensagemId !== messageId) {
        return;
      }
      item.ultimaMensagemStatus = status;
    },
    markConversationsListDirty: markListDirty,
    realtimeConversationUpdated: (
      state,
      action: PayloadAction<{
        conversaId: string;
        mode?: ConversaMode;
        humanSupportReason?: string | null;
        humanSupportSummary?: string | null;
        idResponsavel?: string | null;
        ultimoResponsavelNome?: string | null;
        origemEncerrada?: boolean;
        numeroProcesso?: string | null;
        requeridos?: string[];
        vinculos?: InternalChatVinculo[];
        ultimaMensagem?: string | null;
        dataUltimaMensagem?: string | null;
        isArchived?: boolean;
      }>,
    ) => {
      const patch = action.payload;
      const apply = (
        target: InternalChatActiveListItem | InternalChatConversaDetails | undefined,
      ) => {
        if (!target) return;
        if (patch.mode !== undefined) target.mode = patch.mode;
        if (patch.humanSupportReason !== undefined)
          target.humanSupportReason = patch.humanSupportReason;
        if (patch.humanSupportSummary !== undefined)
          target.humanSupportSummary = patch.humanSupportSummary;
        if (patch.idResponsavel !== undefined) target.idResponsavel = patch.idResponsavel;
        if (patch.ultimoResponsavelNome !== undefined)
          target.ultimoResponsavelNome = patch.ultimoResponsavelNome;
        if (patch.origemEncerrada !== undefined) target.origemEncerrada = patch.origemEncerrada;
        if (patch.numeroProcesso !== undefined) target.numeroProcesso = patch.numeroProcesso;
        if (patch.requeridos !== undefined) target.requeridos = patch.requeridos;
        if (patch.vinculos !== undefined) target.vinculos = patch.vinculos;
        if (patch.ultimaMensagem !== undefined) target.ultimaMensagem = patch.ultimaMensagem;
        if (patch.dataUltimaMensagem !== undefined)
          target.dataUltimaMensagem = patch.dataUltimaMensagem;
        if (patch.isArchived !== undefined) target.isArchived = patch.isArchived;
      };
      apply(state.items.find((c) => c.id === patch.conversaId));
      if (state.selectedDetails?.id === patch.conversaId) apply(state.selectedDetails);
      markListDirty(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state, action) => {
        state.latestListRequestId = action.meta.requestId;
        if (!action.meta.arg.background) state.isLoadingList = true;
        state.errorList = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.isLoadingList = false;
        const recebidas = action.payload.items;
        const items = action.meta.arg.background
          ? manterSelecionadaNaPosicao(state.items, recebidas, state.selectedId)
          : manterSelecionadaNaLista(state.items, recebidas, state.selectedId);
        if (state.selectedId && !recebidas.some((item) => item.id === state.selectedId)) {
          const idx = items.findIndex((item) => item.id === state.selectedId);
          const aberta = idx >= 0 ? items[idx] : undefined;
          if (aberta) items[idx] = { ...aberta, match: undefined };
        }
        state.items = items;
        state.total = action.payload.total;
        state.limit = action.payload.limit;
        state.offset = action.payload.offset;
        state.hasMore = action.payload.hasMore;
        state.nextCursor = action.payload.nextCursor ?? null;
        if (action.payload.segmentCounts) {
          state.segmentCounts = action.payload.segmentCounts;
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.isLoadingList = false;
        state.errorList = action.payload ?? 'Erro ao carregar conversas';
      })
      .addCase(loadMoreConversations.pending, (state) => {
        state.isLoadingMore = true;
      })
      .addCase(loadMoreConversations.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        state.items.push(...action.payload.items);
        state.offset = action.payload.offset;
        state.total = action.payload.total;
        state.hasMore = action.payload.hasMore;
        state.nextCursor = action.payload.nextCursor ?? null;
      })
      .addCase(loadMoreConversations.rejected, (state) => {
        state.isLoadingMore = false;
      })
      .addCase(resumeConversationTemplate.fulfilled, (state, action) => {
        const conversaId = action.payload.conversaId;
        const idx = state.items.findIndex((c) => c.id === conversaId);
        if (idx >= 0 && state.items[idx]) {
          state.items[idx] = { ...state.items[idx], isArchived: false, mode: 'HUMAN' };
        }
        if (state.selectedDetails?.id === conversaId) {
          state.selectedDetails = {
            ...state.selectedDetails,
            isArchived: false,
            mode: 'HUMAN',
          };
        }
        markListDirty(state);
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const m = action.payload;
        patchListLastMessage(state, {
          conversaId: m.conversaId,
          ultimaMensagem: m.conteudo,
          dataUltimaMensagem: m.dataMensagem,
          isInbound: m.direcao === 'INBOUND',
          ultimoResponsavelNome: m.direcao === 'OUTBOUND' && m.senderId ? m.senderNome : undefined,
          ultimaMensagemId: m.id,
          ultimaMensagemStatus: m.status,
          ultimaMensagemDirecao: m.direcao,
        });
      })
      .addCase(fetchConversationDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.errorDetails = null;
      })
      .addCase(fetchConversationDetails.fulfilled, (state, action) => {
        state.isLoadingDetails = false;
        const conversaId =
          typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.conversaId;
        if (state.selectedId !== conversaId || action.payload.id !== conversaId) {
          if (state.selectedDetails?.id !== state.selectedId) state.selectedDetails = null;
          return;
        }
        state.selectedDetails = action.payload;
      })
      .addCase(fetchConversationDetails.rejected, (state, action) => {
        state.isLoadingDetails = false;
        state.errorDetails = action.payload ?? 'Erro ao carregar conversa';
      })
      .addCase(archiveConversation.fulfilled, (state, action) => {
        const conversaId = action.meta.arg;
        const idx = state.items.findIndex((c) => c.id === conversaId);
        if (idx >= 0 && state.items[idx]) {
          state.items[idx] = { ...state.items[idx], isArchived: true };
        }
        if (state.selectedDetails?.id === conversaId) {
          state.selectedDetails = { ...state.selectedDetails, isArchived: true };
        }
        markListDirty(state);
      })
      .addCase(unarchiveConversation.fulfilled, (state, action) => {
        state.items = state.items.filter((c) => c.id !== action.payload.id);
        if (state.selectedId === action.payload.id) {
          state.selectedId = null;
          state.selectedDetails = null;
        }
      })
      .addCase(transferConversation.fulfilled, (state, action) => {
        const idx = state.items.findIndex((c) => c.id === action.payload.id);
        if (idx < 0) return;
        const current = state.items[idx];
        if (!current) return;
        state.items[idx] = { ...current, idResponsavel: action.payload.idResponsavel };
      })
      .addCase(assignConversationResponsavel.fulfilled, (state, action) => {
        const idx = state.items.findIndex((c) => c.id === action.payload.id);
        if (idx < 0) return;
        const current = state.items[idx];
        if (!current) return;
        state.items[idx] = { ...current, idResponsavel: action.payload.idResponsavel };
      })
      .addCase(assumirAtendimento.fulfilled, (state, action) =>
        applyConversaModeResult(state, action.payload),
      )
      .addCase(retomarBot.fulfilled, (state, action) =>
        applyConversaModeResult(state, action.payload),
      )
      .addCase(fetchLinhas.fulfilled, (state, action) => {
        state.linhas = action.payload;
      })
      .addCase(updateLinha.fulfilled, (state, action) => {
        const idx = state.linhas.findIndex((l) => l.id === action.payload.id);
        if (idx >= 0) {
          state.linhas[idx] = action.payload;
        }
      })
      .addCase(fetchBotFlowsElegiveis.pending, (state, action) => {
        const key = botFlowsElegiveisQueryKey(action.meta.arg);
        state.botFlowsElegiveis[key] = {
          data: state.botFlowsElegiveis[key]?.data ?? null,
          status: 'loading',
          error: null,
          requestId: action.meta.requestId,
        };
      })
      .addCase(fetchBotFlowsElegiveis.fulfilled, (state, action) => {
        const entry = state.botFlowsElegiveis[botFlowsElegiveisQueryKey(action.meta.arg)];
        if (entry?.requestId !== action.meta.requestId) return;
        entry.data = action.payload;
        entry.status = 'success';
      })
      .addCase(fetchBotFlowsElegiveis.rejected, (state, action) => {
        const entry = state.botFlowsElegiveis[botFlowsElegiveisQueryKey(action.meta.arg)];
        if (entry?.requestId !== action.meta.requestId) return;
        entry.status = 'error';
        entry.error = action.payload ?? 'Erro ao carregar bots elegíveis';
      });
  },
});

export const {
  selectConversation,
  clearListError,
  clearDetailsError,
  setSortMode,
  markConversationsListDirty,
  realtimeConversationUpserted,
  realtimeConversationUpdated,
  realtimeConversationMessageStatus,
} = conversationsSlice.actions;
export const conversationsReducer = conversationsSlice.reducer;
export type { ConversationsState };
